import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  getFilteredRowModel,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "../ui/table";
import { useDebounce } from "../../hooks/useDebounce";
import { MdSearch } from "react-icons/md";
import TruncatedCell from "../TruncatedCell";

// Checkbox component (replace with your UI lib if needed)
const Checkbox = ({ checked, indeterminate, onChange }) => (
  <input
    type="checkbox"
    checked={checked}
    ref={(el) => {
      if (el) el.indeterminate = indeterminate;
    }}
    onChange={onChange}
    style={{
      cursor: "pointer",
      width: "12px",
      height: "12px",
      accentColor: "#0f172a",
    }}
  />
);

const DataTable = ({
  columns,
  data = [],
  onSelectionChange,
  enableRowSelection = false,
  externalFilters = {},
  filtersSlot = null,
}) => {
  const [sorting, setSorting] = React.useState([]);
  const [rowSelection, setRowSelection] = React.useState({});

  // Add selection column as the first column if enabled
  const selectionColumn = React.useMemo(
    () => ({
      id: "select",
      header: ({ table }) => {
        const isAll = table.getIsAllPageRowsSelected();
        const isSome = table.getIsSomePageRowsSelected();

        return (
          <Checkbox
            checked={isAll}
            indeterminate={isSome}
            onChange={() => {
              if (isAll || isSome) {
                // Clear all if everything is selected OR partially selected
                table.toggleAllPageRowsSelected(false);
              } else {
                // Select all if nothing is selected
                table.toggleAllPageRowsSelected(true);
              }
            }}
          />
        );
      },

      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      width: "20px",
      enableSorting: false,
      enableHiding: false,
    }),
    []
  );

  const allColumns = React.useMemo(
    () => (enableRowSelection ? [selectionColumn, ...columns] : columns),
    [selectionColumn, columns, enableRowSelection]
  );

  const [globalFilter, setGlobalFilter] = useState("");
  const debouncedFilter = useDebounce(globalFilter, 400); // ⏳ delay search

  const [columnFilters, setColumnFilters] = useState([]);

  // Merge all state into a single object to avoid duplicate key
  const tableState = enableRowSelection
    ? { sorting, rowSelection, globalFilter: debouncedFilter, columnFilters }
    : { sorting, globalFilter: debouncedFilter, columnFilters };

  useEffect(() => {
    const filters = Object.entries(externalFilters).map(([id, value]) => ({
      id,
      value,
    }));

    // Only update if filters are actually different
    const isDifferent =
      filters.length !== columnFilters.length ||
      filters.some(
        (f, i) =>
          f.id !== columnFilters[i]?.id || f.value !== columnFilters[i]?.value
      );

    if (isDifferent) {
      setColumnFilters(filters);
    }
  }, [externalFilters, columnFilters]); // ✅ only externalFilters

  const table = useReactTable({
    data,
    columns: allColumns,
    state: tableState,
    onSortingChange: setSorting,
    ...(enableRowSelection && { onRowSelectionChange: setRowSelection }),
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    enableRowSelection,
  });

  React.useEffect(() => {
    if (enableRowSelection && onSelectionChange) {
      // Get selected row IDs (assuming each row has an 'id' field)
      const selectedIds = table
        .getSelectedRowModel()
        .rows.map((row) => row.original.id);
      onSelectionChange(selectedIds);
    }
  }, [rowSelection, table, onSelectionChange, enableRowSelection]);

  // Calculate even width for each column, but allow override via columnDef.width
  //   const colCount = allColumns.length;
  //   const defaultColWidth = `${100 / colCount}%`;

  // Separate fixed-width columns (like checkboxes) from flexible ones
  const fixedWidths = allColumns
    .map((col) => col.width)
    .filter((w) => w && typeof w === "string" && w.endsWith("px"))
    .map((w) => parseInt(w, 10));

  const totalFixedWidth = fixedWidths.reduce((a, b) => a + b, 0);
  const flexibleColCount = allColumns.filter((col) => !col.width).length;

  // Distribute remaining space evenly
  const defaultColWidth = flexibleColCount
    ? `calc((100% - ${totalFixedWidth}px) / ${flexibleColCount})`
    : "auto";

  return (
    <div className="overflow-auto">
      <div className="border border-neutral3 rounded-lg min-w-[1200px]">
        <div className="flex items-center gap-3 justify-end p-2">
          {/* ✅ Custom filter controls injected here */}
          {filtersSlot}

          {/* 🔎 Search bar */}
          <div className="flex items-center focus-within:ring-1 focus:border-[#60B8D01A] border border-input h-[36px] px-2 rounded-lg w-[250px]">
            <input
              type="text"
              placeholder={"Search"}
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="md:px-4 px-2 w-full text-sm focus:outline-none peer"
            />

            <MdSearch color="grey" className="cursor-pointer" size={24} />
          </div>
        </div>

        <Table style={{ tableLayout: "fixed", width: "100%" }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const width =
                    header.column.columnDef.width || defaultColWidth;
                  return (
                    <TableHead
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                        width,
                        maxWidth: width,
                        minWidth: width,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted()
                        ? header.column.getIsSorted() === "asc"
                          ? " ▲"
                          : " ▼"
                        : ""}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell, idx) => {
                    // Find the header for this cell's column
                    const header = table.getHeaderGroups()[0].headers[idx];
                    const width =
                      header?.column?.columnDef?.width || defaultColWidth;
                    const isSelectionCol = cell.column.id === "select";
                    return (
                      <TableCell
                        key={cell.id}
                        style={{
                          width,
                          maxWidth: width,
                          minWidth: width,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          ...(isSelectionCol
                            ? {}
                            : { textOverflow: "ellipsis" }),
                        }}
                      >
                        <div className="min-h-[35px] flex items-center">
                          {isSelectionCol ? (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          ) : cell.column.columnDef.disableTruncate ? (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          ) : (
                            <TruncatedCell>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TruncatedCell>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="text-center py-6 text-neutral-500"
                >
                  No results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default DataTable;
